import { useInfiniteQuery, useQuery, useQueryClient } from '@tanstack/react-query';
import { useIntl } from 'react-intl';
import config from '@/config';
import api from '@/shared/api';
import handleHttpError from '@/shared/services/HandleHttpError';
import { historyParams, nextPageParam, overdueInfoParams, overdueListParams } from '../helpers';
import { remindersOverviewKeys } from './queryKeys';
const http = api.base;
const apiUrl = config.appUrls.api;
export const INVOICE_PAGE_SIZE = 50;
export const GROUPED_PAGE_SIZE = 500;
export const HISTORY_PAGE_SIZE = 50;
export const UPCOMING_HORIZON_DAYS = 120;
const useErrorHandler = () => {
    const intl = useIntl();
    return (error) => handleHttpError(intl)(error);
};
export const useGetRemindersOverviewInfo = (filters) => {
    const onError = useErrorHandler();
    const params = overdueInfoParams(filters);
    return useQuery(remindersOverviewKeys.info(params), ({ signal }) => http
        .get(`${apiUrl}/api/ReminderOverview/Info`, { params, signal })
        .then(res => res.data), { onError, retry: false, keepPreviousData: true });
};
export const useGetOverdueInvoices = (filters, enabled = true) => {
    const onError = useErrorHandler();
    const take = filters.groupPerRelation ? GROUPED_PAGE_SIZE : INVOICE_PAGE_SIZE;
    const params = Object.assign(Object.assign({}, overdueListParams(filters)), { sort: ['expiryDate', 'number'], desc: false, take });
    return useInfiniteQuery(remindersOverviewKeys.invoices(params), ({ pageParam = 0, signal }) => http
        .get(`${apiUrl}/api/ReminderOverview/Invoices`, {
        params: Object.assign(Object.assign({}, params), { page: pageParam }),
        signal,
    })
        .then(res => res.data), {
        enabled,
        onError,
        retry: false,
        keepPreviousData: true,
        getNextPageParam: nextPageParam,
    });
};
export const useGetUpcomingReminders = ({ days = UPCOMING_HORIZON_DAYS, enabled = true } = {}) => {
    const onError = useErrorHandler();
    return useQuery(remindersOverviewKeys.upcoming(days), ({ signal }) => http
        .get(`${apiUrl}/api/ReminderOverview/Upcoming`, {
        params: { days },
        signal,
    })
        .then(res => res.data || []), { enabled, onError, retry: false });
};
export const useGetReminderHistory = (filters) => {
    const onError = useErrorHandler();
    const params = Object.assign(Object.assign({}, historyParams(filters)), { sort: ['sentDate', 'emailId', 'invoiceNr'], desc: true, take: HISTORY_PAGE_SIZE });
    return useInfiniteQuery(remindersOverviewKeys.history(params), ({ pageParam = 0, signal }) => http
        .get(`${apiUrl}/api/ReminderOverview/History`, {
        params: Object.assign(Object.assign({}, params), { page: pageParam }),
        signal,
    })
        .then(res => res.data), {
        onError,
        retry: false,
        keepPreviousData: true,
        getNextPageParam: nextPageParam,
    });
};
export const useGetReminderHistoryStats = () => {
    const onError = useErrorHandler();
    return useQuery(remindersOverviewKeys.historyStats(), ({ signal }) => http
        .get(`${apiUrl}/api/ReminderOverview/HistoryStats`, { signal })
        .then(res => res.data), { onError, retry: false });
};
export const CUSTOMER_PAGE_SIZE = 50;
export const useGetCustomerBehaviour = (filters) => {
    const onError = useErrorHandler();
    const params = {
        relationId: filters.relationId || undefined,
        ownCompanies: filters.companies.length ? filters.companies : undefined,
        months: filters.months,
        minPaidInvoices: filters.minPaidInvoices,
        minAvgDaysLate: filters.minAvgDaysLate,
        // Ordering is fixed server-side: slowest payers first.
        take: CUSTOMER_PAGE_SIZE,
    };
    return useInfiniteQuery(remindersOverviewKeys.customers(params), ({ pageParam = 0, signal }) => http
        .get(`${apiUrl}/api/ReminderOverview/Customers`, {
        params: Object.assign(Object.assign({}, params), { page: pageParam }),
        signal,
    })
        .then(res => res.data), {
        onError,
        retry: false,
        keepPreviousData: true,
        getNextPageParam: nextPageParam,
    });
};
export const useGetReminderRules = () => {
    const onError = useErrorHandler();
    return useQuery(remindersOverviewKeys.rules(), ({ signal }) => http
        .get(`${apiUrl}/api/ReminderOverview/Rules`, { signal })
        .then(res => res.data || []), { onError, retry: false, staleTime: 5 * 60 * 1000 });
};
export const useRefreshRemindersOverview = () => {
    const queryClient = useQueryClient();
    return () => queryClient.invalidateQueries({ queryKey: remindersOverviewKeys.all });
};
